import { useEffect, useState } from "react";
import * as http from "../../../utils/http";

export type DocumentsRequired = "photos_and_id" | "photos" | "id" | "none";

/** HL-12997: the open verification tasks, in the order the page lists them (same names as the server's open-task list). */
export type ThankyouTask = "photos" | "id" | "proof";

export type ThankyouDocuments = {
    /** False until the check settles; the page shows the No card layout meanwhile. */
    settled: boolean;
    /** True once a check came back with nothing outstanding, while the retries for the webhook race still run. */
    answered: boolean;
    /** HL-12997: the check failed, so the page shows the generic My Account copy instead of a task list. */
    failed: boolean;
    needsPhoto: boolean;
    needsId: boolean;
    needsProof: boolean;
};

// The payment webhook moves the order to "waiting for customer actions"; the thank-you page can load
// first, so a not-yet-waiting order is asked again a few times before it counts as "nothing outstanding".
const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 3;

export const getDocumentsRequired = ({ needsPhoto, needsId }: Pick<ThankyouDocuments, "needsPhoto" | "needsId">): DocumentsRequired =>
    needsPhoto && needsId ? "photos_and_id" : needsPhoto ? "photos" : needsId ? "id" : "none";

export const getOpenTasks = ({ needsPhoto, needsId, needsProof }: Pick<ThankyouDocuments, "needsPhoto" | "needsId" | "needsProof">): ThankyouTask[] => [
    ...(needsPhoto ? ["photos" as const] : []),
    ...(needsId ? ["id" as const] : []),
    ...(needsProof ? ["proof" as const] : []),
];

/**
 * HL-12639: which verification tasks are outstanding, from the check My Account uses
 * (GET pending-actions). HL-12997: a failed check sets `failed` (generic copy pointing to My Account).
 */
export default function useThankyouDocuments(country: string | undefined, hashID: string | undefined, enabled: boolean): ThankyouDocuments {
    const [documents, setDocuments] = useState<ThankyouDocuments>({
        settled: false, answered: false, failed: false, needsPhoto: false, needsId: false, needsProof: false,
    });

    useEffect(() => {
        if (!enabled || !country || !hashID) return;
        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;

        const check = (attempt: number) => {
            http
                .getFromServer(`${country}/account/orders/${hashID}/pending-actions`, [http.MIDDLEWARES.CUSTOMER_ID])
                .then((res: any) => {
                    if (cancelled) return;
                    const needsPhoto = res.data?.needsPhotoUpload === true;
                    const needsId = res.data?.needsIdVerification === true;
                    const needsProof = res.data?.needsDoseProof === true;
                    if (!needsPhoto && !needsId && !needsProof && res.data?.isWaitingActions !== true && attempt < MAX_RETRIES) {
                        setDocuments((current) => (current.answered ? current : { ...current, answered: true }));
                        retryTimer = setTimeout(() => check(attempt + 1), RETRY_DELAY_MS);
                        return;
                    }
                    setDocuments({ settled: true, answered: true, failed: false, needsPhoto, needsId, needsProof });
                })
                .catch(() => {
                    if (!cancelled) setDocuments({ settled: true, answered: false, failed: true, needsPhoto: false, needsId: false, needsProof: false });
                });
        };
        check(0);

        return () => {
            cancelled = true;
            clearTimeout(retryTimer);
        };
    }, [country, hashID, enabled]);

    return documents;
}
